import { useCallback, useEffect, useState } from "react";
import _ from 'lodash';
import PropTypes from "prop-types";

import withMeta from "../utils/withMeta";
import Button from "../buttons/Button";
import LoadingPanel from "../LoadingPanel";
import { fieldFormat, isDateType, parseValue } from "../utils/helper";
import { dateToStr, getFormat } from '../utils/convert';

import "./FormDataTable.scss";

const maxCount = 100;

/**
 * Form Data Table
 * @module
 * @param {object} detailData - Detail data
 * @param {array} schema - schema formulára
 * @param {string} primaryField - identifikátor formulára
 * @param {number} currentPage - Current index of page
 * @param {number} myPage - Index of component's page
 * @param {object} frms - objekt s väzbami na iné formuláre
 * @param {func} handleChangeFrm - Function to open new window with podr frm
 */
const FormDataTable = (props) => {

  const { detailData, schema, primaryField, parentPrimaryField, currentPage, myPage, frms, handleChangeFrm } = props;
  const [isLoading, setLoading] = useState(false);
  const [myData, setMyData] = useState({});
  const [mySchema, setMySchema] = useState([]);

  const getFixedFilter = () => {

    const myJoins = frms?.Joins ?? [];
    let filters = [];

    myJoins.forEach(x => {
      const join = structuredClone(x);
      let val;
      if (join.Value !== null) {
        const toReplace = join.Value.match(/{([^}]+)}/g);
        if (!toReplace) {
          val = parseValue(join.Value, join.Type);
        }
        else {
          let finalString = join.Value;
          if (toReplace) {
            toReplace.forEach((element) => {
              let propName = element.replace(/[{}]/g, "");
              const replaceWith = _.get(detailData, propName);
              finalString = finalString.replace(
                element,
                replaceWith != null ? replaceWith : ""
              );
            });
          }

          val = finalString;
          const valueArray = val.split(",");
          if (valueArray.every(x => x === valueArray[0])) {
            val = valueArray[0];
            join.Operator = (join.Operator === "in" || join.Operator === "notin") ? "eq" : join.Operator;
          }
        }
      } else {
        val = _.get(detailData, join.ParentField, null);
        if (val instanceof Array) {
          val = val.join();
        }
      }

      if (val === null) val = join.Operator === "eq-ifexists" ? null : "-1";

      filters.push({
        field: join.Field,
        joins: [{ parentField: join.ParentField }],
        value: val,
        operator: join.Operator ? join.Operator.split('-')[0] : 'in',
        filterType: 1,
      });
    });
    filters.push({
      field: "Zrus",
      value: false,
      operator: "eq"
    });

    return { filters };
  };

  // načítanie dát
  const getData = useCallback(async () => {
    setLoading(true);
    setMyData({});
    let res = await props.meta.getList(primaryField, getFixedFilter(), [], { skip: 0, take: maxCount });

    // úprava dátumových stĺpcov
    let dateColumns = mySchema && mySchema.length > 0 ? mySchema.filter(x => isDateType(x.filter)) : schema.filter(x => isDateType(x.filter));
    if (dateColumns.length > 0) {
      res.Data.Items.map(x => {
        dateColumns.forEach(column => {
          const value = _.get(x, column.field);
          const filter = column.filter.replace('input', '');
          const format = column.format ? getFormat(fieldFormat(column.format), filter) :
            filter === "time" ? "HH:mm" : filter === "date" ? "dd.MM.yyyy" : "dd.MM.yyyy H:m";
          _.set(x, column.field, dateToStr(!value || value instanceof Date ? value : value.replace(/Z$/, ""), format));
        });
        return x;
      });
    }
    setMyData(res);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailData[parentPrimaryField]]);

  // zoradenie schémy podľa order
  useEffect(() => {
    let sortedSchema = schema.filter(e => e.order !== null && !e.disabled).sort((a, b) => a.order - b.order);
    setMySchema(sortedSchema);
  }, [schema]);

  // načítavanie dát, len ak je záložka otvorená
  useEffect(() => {
    if (currentPage === myPage)
      getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailData[parentPrimaryField], currentPage]);

  // formátovanie dát v tabuľke
  const getValue = (item, el) => {
    if (el.type === 'boolean') {
      return item[el.field] ? "áno" : "nie";
    }

    if (el.type === 'enum') {
      return el.enumData.find(e => e[el.primaryField] === item[el.field])?.[el.textField];
    }

    if (el.type !== 'container') {
      return _.get(item, el.field.split("."));
    }

    if (el.valueField) {
      // ak je objekt prázdy, netreba skúšať skladať text
      if (!item[el.field]) return "";
      let toReplace = el.valueField.match(/{([^}]+)}/g);
      if (!toReplace) {
        return _.get(item, [el.field, el.textField]);
      }
      let finalString = el.valueField;
      toReplace.forEach((element) => {
        let propName = element.replace(/[{}]/g, "");
        const replaceWith = _.get(item, [el.field, propName.split(".")].flat());
        finalString = finalString.replace(
          element,
          replaceWith != null ? replaceWith : ""
        );
      });
      return finalString.trim();
    } else {
      return _.get(item, [el.field, el.textField]);
    }
  };

  // otvorenie veľkého okna pre podradený formulár
  const showFrm = () => {
    //console.log(obj.parentProps.frms.find(x => x.Identif === obj.field.frm));
    handleChangeFrm("formDataTable", frms);
  };

  // sumár načítaných dát
  const giveSummary = (total, count) => {
    if (total === 0) {
      return "Žiadne záznamy";
    }
    return `1 - ${Math.min(total, count)} z ${total} záznamov`;
  };

  return (
    <>
      <LoadingPanel isLoading={isLoading} />

      {frms !== null &&
        <div className="formDataTable_btn">
          <Button
            onClick={showFrm}
            position={"bottom"}
            //imageUrl={buildUrl('assets/icons/toolbar_podriad_formular.svg')}
            title={"Otvoriť podradený formulár v okne"}
            label={"Zobraziť"}
            type={"button"}
          />
        </div>
      }

      <div className="formDataTable">
        <table className="formDataTable_table">
          <thead>
            <tr className="formDataTable_header">
              {(mySchema !== undefined) && mySchema.map(el => {
                return (
                  <th style={{ width: el.width ?? 150 }} key={el.title} >
                    <span>{el.title}</span>
                  </th>
                );
              })}
            </tr>
          </thead>
          {(myData.Data !== undefined && mySchema !== undefined) &&
            <tbody className={myData.Data.TotalRecords > maxCount * .7 ? "l" : (myData.Data.TotalRecords > maxCount * .4 ? "m" : "s")}>
              {myData.Data.Items.map(item => {
                return <tr key={item[primaryField]}>
                  {mySchema.map(el => {
                    return <td key={item[primaryField] + el.title}>{getValue(item, el)}</td>;
                  })}
                </tr>;
              })}
            </tbody>
          }
        </table>
        <div className="formDataTable_summary">{giveSummary(myData.Data?.TotalRecords ?? 0, maxCount)}</div>
      </div >
    </>
  );
};

FormDataTable.propTypes = {
  detailData: PropTypes.object.isRequired,
  schema: PropTypes.array.isRequired,
  primaryField: PropTypes.string.isRequired,
  currentPage: PropTypes.number.isRequired,
  myPage: PropTypes.number,
  frms: PropTypes.object,
  handleChangeFrm: PropTypes.func
};

export default withMeta(FormDataTable);